import React, { useCallback, useRef, useState } from 'react';
import Head from 'expo-router/head';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Pressable,
} from 'react-native';
import { Link, router } from 'expo-router';
import { useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useTheme,
  TYPOGRAPHY,
  ON_COLOR,
  SPACING,
  NARROW_MAX_WIDTH,
  BORDER_RADIUS,
  withWeight,
} from '../../src/theme';
import { Check } from 'lucide-react-native';
import { ContentConstraint } from '@components/layout';
import { Button, ErrorBanner, Input, RediLogo } from '@components/common';
import { GoogleSignInButton } from '@components/auth/GoogleSignInButton';
import { useAuthStore } from '../../src/store/authStore';
import { useUIStore } from '../../src/store/uiStore';
import { USER_ROLE_META, type UserRole } from '../../src/types';
// Rendered as compact chips here because the sign-up form is already tall;
// the full card treatment lives in RoleSelectionSheet.
import { ROLE_ORDER } from '../../src/constants/roleIcons';
import { MIN_PASSWORD_LENGTH } from '../../src/constants/auth';

export default function SignUpScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { signUp, signInWithGoogle, isLoading, error, clearError } = useAuthStore();
  const lastNameRef = useRef<TextInput>(null);
  const emailRef = useRef<TextInput>(null);
  const passwordRef = useRef<TextInput>(null);
  const extraRef = useRef<TextInput>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // A visitor who arrived on an audience intro page (/start/fire-official)
  // gets that role ticked already. Fire Scout is always on. Rules forbid a
  // client adding insurance-agent or insurance-company to `roles`, and those
  // two have no intro page for that reason: if one ever gets one, this
  // prefill has to route through provisionAgentProfile instead.
  const pendingAudience = useUIStore((s) => s.pendingAudience);
  const clearPendingAudience = useUIStore((s) => s.clearPendingAudience);
  const [selectedRoles, setSelectedRoles] = useState<Set<UserRole>>(() =>
    pendingAudience
      ? new Set<UserRole>(['fire-scout', pendingAudience])
      : new Set<UserRole>(['fire-scout'])
  );
  const [insurerName, setInsurerName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [googleBusy, setGoogleBusy] = useState(false);

  useFocusEffect(
    useCallback(() => {
      clearError();
    }, [clearError])
  );

  const toggleRole = (r: UserRole) => {
    if (r === 'fire-scout') return;
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
  };

  const isAgent = selectedRoles.has('insurance-agent');

  const handleSignUp = async () => {
    if (!firstName || !lastName || !email || !password) return;
    if (isAgent && !insurerName.trim()) return;
    try {
      await signUp({
        email,
        password,
        firstName,
        lastName,
        roles: Array.from(selectedRoles),
        insurerName: isAgent ? insurerName.trim() : undefined,
        inviteCode: !isAgent && inviteCode.trim() ? inviteCode.trim() : undefined,
      });
      clearPendingAudience();
      router.replace('/(tabs)');
    } catch {
      // Error is handled by store
    }
  };

  // The invite code above applies only to the email sign-up path — Google's
  // popup has no field for it. The audience from an intro page DOES carry
  // through, because authApi.signInWithGoogle reads the same store; without
  // that, arriving via /start/fire-official and then pressing the Google
  // button silently dropped the role.
  const handleGoogle = async () => {
    setGoogleBusy(true);
    try {
      await signInWithGoogle(pendingAudience ? [pendingAudience] : undefined);
      clearPendingAudience();
    } catch {
      // Error surfaces via the store's error state (ErrorBanner below).
    } finally {
      setGoogleBusy(false);
    }
  };

  const isValid =
    firstName &&
    lastName &&
    email &&
    password.length >= MIN_PASSWORD_LENGTH &&
    (!isAgent || insurerName.trim().length > 0);

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <Head>
        <title>Sign Up - Redi</title>
      </Head>
      <ContentConstraint maxWidth={NARROW_MAX_WIDTH}>
        <ScrollView
          contentContainerStyle={[styles.scrollContent, { paddingTop: insets.top + SPACING.xl }]}
          keyboardShouldPersistTaps="handled">
          <View style={styles.header}>
            <RediLogo fontSize={20} style={{ marginBottom: SPACING.xl + SPACING.sm }} />
            <Text style={[styles.title, { color: colors.text }]}>Create account</Text>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
              Community wildfire defense
            </Text>
          </View>

          <View style={styles.form}>
            {/* Role picker: all 9 roles, Fire Scout locked on, extras optional */}
            <View style={styles.roleSection}>
              <Text style={[styles.roleLabel, { color: colors.textSecondary }]}>
                You&apos;re a Fire Scout. What other roles do you play?
              </Text>
              <View style={styles.roleRow}>
                {ROLE_ORDER.map((r) => {
                  const selected = selectedRoles.has(r);
                  const locked = r === 'fire-scout';
                  return (
                    <Pressable
                      key={r}
                      onPress={() => toggleRole(r)}
                      testID={`role-chip-${r}`}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: selected, disabled: locked }}
                      // RN-web drops accessibilityState, so the aria props are
                      // the only thing a screen reader (or the audit) sees on
                      // web. Both kept: native reads the first, web the second.
                      aria-checked={selected}
                      aria-disabled={locked}
                      style={[
                        styles.rolePill,
                        {
                          backgroundColor: selected ? colors.primary : 'transparent',
                          borderColor: selected ? colors.primary : colors.border,
                        },
                      ]}>
                      {selected && <Check size={13} color={ON_COLOR.text} />}
                      <Text
                        style={[
                          styles.rolePillText,
                          { color: selected ? ON_COLOR.text : colors.text },
                        ]}>
                        {USER_ROLE_META[r].label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
              <Text style={[styles.roleHint, { color: colors.textTertiary }]}>
                Optional. You can change these anytime from your profile.
              </Text>
            </View>

            <View style={styles.nameRow}>
              <View style={styles.nameField}>
                <Input
                  label="First name"
                  value={firstName}
                  onChangeText={setFirstName}
                  placeholder="First"
                  autoCapitalize="words"
                  returnKeyType="next"
                  onSubmitEditing={() => lastNameRef.current?.focus()}
                  blurOnSubmit={false}
                />
              </View>
              <View style={styles.nameField}>
                <Input
                  ref={lastNameRef}
                  label="Last name"
                  value={lastName}
                  onChangeText={setLastName}
                  placeholder="Last"
                  autoCapitalize="words"
                  returnKeyType="next"
                  onSubmitEditing={() => emailRef.current?.focus()}
                  blurOnSubmit={false}
                />
              </View>
            </View>

            <Input
              ref={emailRef}
              label="Email"
              value={email}
              onChangeText={setEmail}
              placeholder="Enter your email"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="next"
              onSubmitEditing={() => passwordRef.current?.focus()}
              blurOnSubmit={false}
            />

            <Input
              ref={passwordRef}
              label="Password"
              value={password}
              onChangeText={setPassword}
              placeholder="Create a password"
              secureTextEntry
              returnKeyType="next"
              onSubmitEditing={() => extraRef.current?.focus()}
              hint={`At least ${MIN_PASSWORD_LENGTH} characters`}
            />

            {isAgent ? (
              <Input
                ref={extraRef}
                label="Insurance company name"
                value={insurerName}
                onChangeText={setInsurerName}
                placeholder="e.g. Acme Mutual"
                autoCapitalize="words"
                autoCorrect={false}
                returnKeyType="done"
                onSubmitEditing={handleSignUp}
              />
            ) : (
              // Same wording as the sheet's copy of this field in
              // AuthGateBottomSheet.tsx. Change both together.
              <Input
                ref={extraRef}
                label="Invite code from your insurance agent"
                value={inviteCode}
                onChangeText={setInviteCode}
                placeholder="Optional"
                autoCapitalize="characters"
                autoCorrect={false}
                returnKeyType="done"
                onSubmitEditing={handleSignUp}
              />
            )}

            <ErrorBanner message={error} style={styles.errorSpacing} />

            <Button
              title="Create Account"
              onPress={handleSignUp}
              loading={isLoading}
              disabled={!isValid}
              fullWidth
              size="lg"
            />
          </View>

          <View style={styles.dividerRow}>
            <View style={[styles.dividerLine, { backgroundColor: colors.divider }]} />
            <Text style={[styles.dividerText, { color: colors.textTertiary }]}>or</Text>
            <View style={[styles.dividerLine, { backgroundColor: colors.divider }]} />
          </View>

          {/* Wrapper is web-gated too: the button renders null on native,
              but the wrap's marginBottom would still add phantom spacing. */}
          {Platform.OS === 'web' && (
            <View style={styles.googleButtonWrap}>
              <GoogleSignInButton
                testID="signup-google"
                loading={googleBusy}
                onPress={handleGoogle}
              />
            </View>
          )}

          <View style={styles.footer}>
            <Text style={[styles.footerText, { color: colors.textSecondary }]}>
              Already have an account?{' '}
            </Text>
            <Link href="/(auth)/sign-in" asChild>
              <TouchableOpacity accessibilityRole="link" accessibilityLabel="Go to sign in">
                <Text style={[styles.linkText, { color: colors.text }]}>Sign in</Text>
              </TouchableOpacity>
            </Link>
          </View>
        </ScrollView>
      </ContentConstraint>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: SPACING.lg,
  },
  header: {
    alignItems: 'flex-start',
    marginBottom: SPACING.xl,
  },
  title: {
    ...TYPOGRAPHY.screenTitle,
    marginBottom: SPACING.xs,
  },
  subtitle: {
    ...TYPOGRAPHY.bodySmall,
  },
  form: {},
  roleSection: {
    marginBottom: SPACING.md,
  },
  roleLabel: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.sm,
  },
  roleRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
  },
  rolePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingVertical: SPACING.xs,
    paddingHorizontal: SPACING.smd,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
  },
  rolePillText: {
    ...TYPOGRAPHY.label,
  },
  roleHint: {
    ...TYPOGRAPHY.caption,
    marginTop: SPACING.sm,
  },
  nameRow: {
    flexDirection: 'row',
    gap: SPACING.smd,
  },
  nameField: {
    flex: 1,
  },
  errorSpacing: {
    marginBottom: SPACING.md,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: SPACING.lg,
  },
  dividerLine: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
  },
  dividerText: {
    ...TYPOGRAPHY.bodySmall,
    marginHorizontal: SPACING.md,
  },
  googleButtonWrap: {
    marginBottom: SPACING.lg,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  footerText: {
    ...TYPOGRAPHY.body,
  },
  linkText: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
});
